import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { 
  Building2, Plus, Search, Users, FolderKanban, 
  MoreHorizontal, Edit2, Eye, Loader2, CheckCircle2, XCircle
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const AdminOrganizationsPage = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    org_number: "",
    address: ""
  });

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const getToken = () => localStorage.getItem("admin_token");

  const fetchOrganizations = async () => {
    try {
      const res = await fetch(`${API}/admin/organizations`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.ok) {
        setOrganizations(await res.json());
      }
    } catch (err) {
      console.error("Failed to fetch organizations:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/admin/organizations`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}` 
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success("Organisation skapad!");
        setDialogOpen(false);
        setFormData({ name: "", org_number: "", address: "" });
        fetchOrganizations();
      } else {
        toast.error("Kunde inte skapa organisation");
      }
    } catch (err) {
      toast.error("Ett fel uppstod");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;

    try {
      const res = await fetch(`${API}/admin/organizations/${selectedOrg.organization_id}`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}` 
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success("Organisation uppdaterad!");
        setEditDialogOpen(false);
        setSelectedOrg(null);
        fetchOrganizations();
      } else {
        toast.error("Kunde inte uppdatera organisation");
      }
    } catch (err) {
      toast.error("Ett fel uppstod");
    }
  };

  const toggleStatus = async (org) => {
    const newStatus = org.status === "active" ? "inactive" : "active";
    try {
      const res = await fetch(`${API}/admin/organizations/${org.organization_id}`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}` 
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        toast.success(newStatus === "active" ? "Organisation aktiverad" : "Organisation inaktiverad");
        fetchOrganizations();
      }
    } catch (err) {
      toast.error("Ett fel uppstod");
    }
  };

  const openEditDialog = (org) => {
    setSelectedOrg(org);
    setFormData({
      name: org.name,
      org_number: org.org_number || "",
      address: org.address || ""
    });
    setEditDialogOpen(true);
  };

  const filteredOrgs = organizations.filter(org =>
    org.name?.toLowerCase().includes(search.toLowerCase()) ||
    org.org_number?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
            Organisationer
          </h1>
          <p className="text-slate-400 mt-1">{organizations.length} kunder totalt</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-600 text-black">
              <Plus className="w-4 h-4 mr-2" />
              Ny organisation
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
                Skapa ny organisation
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Företagsnamn *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="Byggföretaget AB"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Organisationsnummer</Label>
                <Input
                  value={formData.org_number}
                  onChange={(e) => setFormData({ ...formData, org_number: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="556123-4567"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Adress</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="Byggvägen 1, 123 45 Stockholm"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" className="flex-1 border-slate-600 text-slate-300" onClick={() => setDialogOpen(false)}>
                  Avbryt
                </Button>
                <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600 text-black">
                  Skapa
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Sök organisation..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
        />
      </div>

      {/* Organizations Grid */}
      <div className="grid gap-4">
        {filteredOrgs.length === 0 ? (
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="py-12 text-center">
              <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">
                {search ? "Inga organisationer hittades" : "Inga organisationer ännu"}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredOrgs.map((org) => (
            <Card key={org.organization_id} className="bg-slate-800 border-slate-700 hover:border-slate-600 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-white">{org.name}</h3>
                        <Badge className={org.status === "active" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                          {org.status === "active" ? "Aktiv" : "Inaktiv"}
                        </Badge>
                      </div>
                      {org.org_number && (
                        <p className="text-sm text-slate-400">Org.nr: {org.org_number}</p>
                      )}
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {org.user_count || 0} användare
                        </span>
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <FolderKanban className="w-3 h-3" />
                          {org.project_count || 0} projekt
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-slate-400 hover:text-white"
                      onClick={() => openEditDialog(org)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={org.status === "active" ? "text-red-400 hover:text-red-300" : "text-green-400 hover:text-green-300"}
                      onClick={() => toggleStatus(org)}
                    >
                      {org.status === "active" ? <XCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              Redigera organisation
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Företagsnamn *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Organisationsnummer</Label>
              <Input
                value={formData.org_number}
                onChange={(e) => setFormData({ ...formData, org_number: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Adress</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1 border-slate-600 text-slate-300" onClick={() => setEditDialogOpen(false)}>
                Avbryt
              </Button>
              <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600 text-black">
                Spara
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
